import { useRef, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { ReactComponent as InviteIcon } from '@elementary/common-ui/assets/invite.svg';
import {
  Loading,
  Flex,
  Button,
  FlexColumn,
  Breadcrumbs,
  Dialog,
  SearchField,
} from '@elementary/common-ui/components';
import OverflowTooltip from '@elementary/common-ui/components/OverflowTooltip';
import { List, Column } from '@elementary/common-ui/components/Table';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { ACCOUNT_USERS } from '@elementary/common-ui/constants/queryKeys';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import { TeamUser, UserInviteForm, UserUpdateForm } from 'shared/types';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import { useRoles } from '../shared/useRoles';
import UserCard from '../shared/UserCard';
import InviteUserModal from './InviteUserModal';
import UpdateUserModal from './UpdateUserModal';
import UserMenu from './UserMenu';

const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  gap: 24px;
`;

const Users = () => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const [deletedUserId, setDeletedUserId] = useState('');
  const [deleteUserName, setDeleteUserName] = useState('');
  const [updateUserId, setUpdateUserId] = useState('');
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [updateUserModalOpen, setUpdateUserModalOpen] = useState(false);
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const resetRef = useRef<{ reset: () => void }>();

  const { data, isLoading } = useQuery([ACCOUNT_USERS], () => api.fetchUsers());

  const { roles } = useRoles();

  const { mutate: deleteUser, isLoading: deleteUserIsLoading } = useMutation(
    (id: string) => api.deleteUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ACCOUNT_USERS]);
        setDeleteUserModalOpen(false);
      },
      onError: () => {
        queryClient.invalidateQueries([ACCOUNT_USERS]);
        setDeleteUserModalOpen(false);
      },
    },
  );

  const {
    mutate: updateUser,
    isLoading: updateUserLoading,
    error: updateError,
  } = useMutation((form: UserUpdateForm) => api.updateUser(form), {
    onSuccess: () => {
      queryClient.invalidateQueries([ACCOUNT_USERS]);
      setUpdateUserModalOpen(false);
      openSnackbar({ message: 'User updated successfully.' });
    },
    onError: () => {
      queryClient.invalidateQueries([ACCOUNT_USERS]);
      setUpdateUserModalOpen(false);
    },
  });

  const {
    mutate: inviteUser,
    isLoading: inviteUserLoading,
    error: inviteError,
    reset: resetError,
  } = useMutation(
    ({ name, email, role_id }: UserInviteForm) =>
      api.inviteUser({ name, email, role_id }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ACCOUNT_USERS]);
        setInviteUserModalOpen(false);
        resetRef?.current?.reset?.();
        openSnackbar({ message: 'Email invitation sent successfully.' });
      },
    },
  );

  const onCreateUserModalClose = useCallback(() => {
    setInviteUserModalOpen(false);
    resetRef?.current?.reset?.();
    resetError();
  }, [resetError]);

  const columns: Array<Column<TeamUser>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'User',
        filterable: false,
        renderer: ({ id, name, email, verified }) => (
          <UserCard id={id} name={name} email={email} verified={verified} />
        ),
        compareFunc: (a, b) => {
          if (a.verified && !b.verified) {
            return 1;
          }
          return a.name.localeCompare(b.name);
        },
      },
      {
        id: 'roles',
        header: 'Role',
        filterable: false,
        showValueWhenEmpty: true,
        width: 180,
        renderer: ({ roles: user_roles }) => {
          const firstRoleId = user_roles[0]?.id;
          const roleName = firstRoleId
            ? roles?.find((role) => role.id === firstRoleId)?.name
            : undefined;
          return <OverflowTooltip>{roleName || '-'}</OverflowTooltip>;
        },
        compareFunc: (a, b) => {
          const roleNameA =
            roles?.find((role) => role.id === a.roles[0]?.id)?.name || '';
          const roleNameB =
            roles?.find((role) => role.id === b.roles[0]?.id)?.name || '';
          return roleNameA.localeCompare(roleNameB);
        },
      },
      {
        id: 'lastLogin',
        header: 'Last Active',
        filterable: false,
        renderer: ({ lastLogin }) => (
          <OverflowTooltip>
            {lastLogin && localizeDate(lastLogin).format('lll')}
          </OverflowTooltip>
        ),
        compareFunc: (a, b) => dateComparer(a.lastLogin, b.lastLogin),
      },
      {
        id: 'menu',
        header: '',
        renderer: ({ id, email, verified }) => (
          <UserMenu
            {...{
              id,
              email,
              verified,
              setUpdateUserId,
              setUpdateUserModalOpen,
              setDeletedUserId,
              setDeleteUserName,
              setDeleteUserModalOpen,
            }}
          />
        ),
        filterable: false,
        sortable: false,
        metaColumn: true,
      },
    ],
    [roles],
  );

  const filteredData = useMemo(
    () =>
      search.trim()
        ? data?.filter(
            (item) => item.email.includes(search) || item.name.includes(search),
          )
        : data,
    [data, search],
  );

  return isLoading ? (
    <Loading />
  ) : (
    <Wrapper>
      <FlexColumn sx={{ gap: 3 }}>
        <Breadcrumbs items={['Account', 'Users']} showTreeButton={false} />
        <Flex sx={{ gap: 1, justifyContent: 'space-between' }}>
          <SearchField
            sx={{ width: '248px' }}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
          <Button
            sx={{ minWidth: '142px' }}
            onClick={() => setInviteUserModalOpen(true)}
            startIcon={<InviteIcon />}
          >
            Invite user
          </Button>
        </Flex>
      </FlexColumn>

      <List columns={columns} data={filteredData} defaultSortKey="name" />

      <Dialog
        open={deleteUserModalOpen}
        onClose={() => setDeleteUserModalOpen(false)}
        onConfirmButtonClick={() => deleteUser(deletedUserId)}
        isLoading={deleteUserIsLoading}
        titleIcon={<DeleteIcon />}
        title="Delete user"
        confirmText="Delete user"
        message={`Are you sure you want to delete ${deleteUserName}?`}
        secondaryMessage="This action cannot be undone."
      />

      <UpdateUserModal
        open={updateUserModalOpen}
        onClose={() => setUpdateUserModalOpen(false)}
        onConfirmButtonClick={(form: UserUpdateForm) => updateUser(form)}
        confirmText="Update user"
        errorMessage={(updateError as any)?.response?.data?.message}
        updateUserLoading={updateUserLoading}
        user={data?.find(
          (team_user: TeamUser) => team_user.id === updateUserId,
        )}
        roles={roles}
      />

      <InviteUserModal
        ref={resetRef}
        open={inviteUserModalOpen}
        onClose={onCreateUserModalClose}
        onConfirmButtonClick={({ name, email, role_id }: UserInviteForm) =>
          inviteUser({ name, email, role_id })
        }
        confirmText="Send invitation"
        errorMessage={(inviteError as any)?.response?.data?.message}
        inviteUserLoading={inviteUserLoading}
        roles={roles}
      />
    </Wrapper>
  );
};

export default Users;
